import { Reference } from '@approvalmax/types';
import {
    CheckboxEditor,
    DateTimeEditor,
    FocusImplementer,
    NumberEditor,
    TextAreaEditor,
    TextEditor,
    TimeEditor,
} from '@approvalmax/ui';
import { constants } from 'modules/common';
import { memo } from 'react';

import { getCustomFieldMaxLength } from '../../helpers';
import { NetSuiteLineItemCustomReferenceCell } from '../NetSuiteLineItemCustomReferenceCell/NetSuiteLineItemCustomReferenceCell';
import { messages } from './NetSuiteLineCustomFieldEditor.messages';
import { NetSuiteLineCustomFieldEditorProps } from './NetSuiteLineCustomFieldEditor.types';

const { netSuiteConstants } = constants;

export const NetSuiteLineCustomFieldEditor = memo((props: NetSuiteLineCustomFieldEditorProps) => {
    const { lineId, field, fieldState, sublistType, isReadonly = false, onChange } = props;

    switch (field.netSuiteField?.type) {
        case 'CheckBox':
            return (
                <CheckboxEditor
                    onChange={(value) => onChange(lineId, field, 'valueBoolean', value)}
                    value={fieldState?.valueBoolean || false}
                    disabled={isReadonly}
                />
            );

        case 'Currency':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <NumberEditor
                            ref={registerFocusable}
                            allowFloat
                            value={fieldState?.valueDecimal || null}
                            onChange={(value) => onChange(lineId, field, 'valueDecimal', value)}
                            placeholder={messages.inputDefaultPlaceholder}
                            displayFormat='currency'
                            max={netSuiteConstants.maxCustomNumericFieldAmount}
                            min={-netSuiteConstants.maxCustomNumericFieldAmount}
                            precision={2}
                            theme='transparent'
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'DecimalNumber':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <NumberEditor
                            ref={registerFocusable}
                            allowFloat
                            value={fieldState?.valueDecimal || null}
                            onChange={(value) => onChange(lineId, field, 'valueDecimal', value)}
                            placeholder={messages.inputDefaultPlaceholder}
                            displayFormat='as-is'
                            max={netSuiteConstants.maxCustomNumericFieldAmount}
                            min={-netSuiteConstants.maxCustomNumericFieldAmount}
                            precision={8}
                            theme='transparent'
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'IntegerNumber':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <NumberEditor
                            ref={registerFocusable}
                            allowFloat={false}
                            value={fieldState?.valueLong || null}
                            onChange={(value) => onChange(lineId, field, 'valueLong', value)}
                            placeholder={messages.inputDefaultPlaceholder}
                            displayFormat='as-is'
                            max={netSuiteConstants.maxCustomNumericFieldAmount}
                            min={-netSuiteConstants.maxCustomNumericFieldAmount}
                            theme='transparent'
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'Percent':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <NumberEditor
                            ref={registerFocusable}
                            allowFloat
                            value={fieldState?.valueDecimal || null}
                            onChange={(value) => onChange(lineId, field, 'valueDecimal', value)}
                            placeholder={messages.inputDefaultPlaceholder}
                            displayFormat='as-is'
                            max={100}
                            min={0}
                            precision={4}
                            theme='transparent'
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'Date':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <DateTimeEditor
                            ref={registerFocusable}
                            value={fieldState?.valueDate || null}
                            placeholder={messages.selectDefaultPlaceholder}
                            onChange={(value) => onChange(lineId, field, 'valueDate', value)}
                            theme='transparent'
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'TimeOfDay':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <TimeEditor
                            ref={registerFocusable}
                            variant='time'
                            value={fieldState?.valueDate || null}
                            theme='transparent'
                            onChange={(value) => onChange(lineId, field, 'valueDate', value)}
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'Datetime':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <TimeEditor
                            ref={registerFocusable}
                            variant='datetime'
                            value={fieldState?.valueDate || null}
                            theme='transparent'
                            onChange={(value) => onChange(lineId, field, 'valueDate', value)}
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'Hyperlink':
        case 'PhoneNumber':
        case 'EMailAddress':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <TextEditor
                            ref={registerFocusable}
                            value={fieldState?.valueString || ''}
                            onChange={(value) => onChange(lineId, field, 'valueString', value)}
                            placeholder={messages.inputDefaultPlaceholder}
                            changeOnBlur
                            theme='transparent'
                            maxLength={getCustomFieldMaxLength(field)}
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'TextArea':
        case 'LongText':
        case 'FreeFormText':
            return (
                <FocusImplementer>
                    {({ registerFocusable }) => (
                        <TextAreaEditor
                            ref={registerFocusable}
                            value={fieldState?.valueString || ''}
                            onChange={(value) => onChange(lineId, field, 'valueString', value)}
                            changeOnBlur
                            minHeight={2}
                            placeholder={messages.inputDefaultPlaceholder}
                            theme='transparent'
                            maxLength={getCustomFieldMaxLength(field)}
                            disabled={isReadonly}
                        />
                    )}
                </FocusImplementer>
            );

        case 'ListRecord': {
            return (
                <NetSuiteLineItemCustomReferenceCell
                    value={fieldState?.valueRef || null}
                    singleLinePerItem={false}
                    placeholder={messages.selectDefaultPlaceholder}
                    onChange={(value: Reference | null) => onChange(lineId, field, 'valueRef', value)}
                    disabled={isReadonly}
                    fieldId={field.id}
                    sublistType={sublistType}
                />
            );
        }

        case 'MultipleSelect': {
            return (
                <NetSuiteLineItemCustomReferenceCell
                    value={fieldState?.valueRefs || null}
                    singleLinePerItem={false}
                    placeholder={messages.selectDefaultPlaceholder}
                    multiple
                    fieldId={field.id}
                    sublistType={sublistType}
                    onChange={(value: Reference[] | null) => onChange(lineId, field, 'valueRefs', value)}
                    disabled={isReadonly}
                />
            );
        }

        default:
            return null;
    }
});

NetSuiteLineCustomFieldEditor.displayName = 'NetSuiteLineCustomFieldEditor';
